import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import styles from "./OurEventPage.module.less";

import akkBackground from "app/assets/akkBackground.webp";
import { useParams } from "react-router";
import Loader from "shared/components/Loader";
import { ArchiveType, useGetArchivesQuery } from "api/ticketsApi";
import Modal from "shared/components/Modal";
import { Zoomable } from "shared/components/Zoomable";

interface Props {
  setBackgroundImage: (src: string) => void;
}

const ITEMS_PER_PAGE = 5;

const OurEventPage = ({ setBackgroundImage }: Props) => {
  const { eventId } = useParams();
  const { data: items, isLoading } = useGetArchivesQuery(undefined);
  const [currentYear, setCurrentYear] = useState<string>("");
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [selectedImage, setSelectedImage] = useState<{src: string, alt: string} | null>(null);
  const [visibleCount, setVisibleCount] = useState<number>(ITEMS_PER_PAGE);

  useEffect(() => {
    setBackgroundImage(akkBackground);
  }, [setBackgroundImage]);

  // Reset when year changes
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
    setLoadedImages(new Set());
  }, [currentYear]);

  // Group archives by year
  const groupedArchives = useMemo(() => {
    if (!items) return {} as Record<string, ArchiveType[]>;

    const grouped = items.reduce((acc, archive) => {
      const year = archive.years;
      if (!acc[year]) {
        acc[year] = [];
      }
      acc[year].push(archive);
      return acc;
    }, {} as Record<string, ArchiveType[]>);

    const sortedYears = Object.keys(grouped).sort((a, b) => parseInt(b) - parseInt(a));

    const reversedGrouped: Record<string, ArchiveType[]> = {};
    sortedYears.forEach(year => {
      reversedGrouped[year] = grouped[year];
    });

    return reversedGrouped;
  }, [items]);

  // Set initial year
  useEffect(() => {
    if (items && items.length > 0 && !currentYear) {
      const years = Object.keys(groupedArchives);
      if (years.length > 0) {
        setCurrentYear(years[0]);
      }
    }
  }, [items, groupedArchives, currentYear]);

  const handleImageLoad = useCallback((imageId: string) => {
    setLoadedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(imageId);
      return newSet;
    });
  }, []);

  const handleImageClick = useCallback((archive: ArchiveType) => {
    setSelectedImage({
      src: archive.imgCover || archive.img,
      alt: archive.name
    });
  }, []);

  const closeModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const currentArchives = groupedArchives[currentYear] || [];
  const visibleArchives = currentArchives.slice(0, visibleCount);
  const hasMore = visibleCount < currentArchives.length;

  // Логика автоматической загрузки следующих элементов
  useEffect(() => {
    if (!hasMore) return;
    if (visibleArchives.length === 0) return;

    // Проверяем, все ли текущие видимые изображения загружены
    const allLoaded = visibleArchives.every((archive) => {
      const imageId = `${currentYear}-${archive.id}`;
      return loadedImages.has(imageId);
    });

    console.log('Checking auto-load:', {
      visibleCount: visibleArchives.length,
      loadedImagesCount: loadedImages.size,
      allLoaded,
      hasMore,
      currentYear
    });

    if (allLoaded) {
      console.log('Loading next batch...');
      // Загружаем следующие 5 элементов
      setVisibleCount(prev => {
        const nextCount = Math.min(prev + ITEMS_PER_PAGE, currentArchives.length);
        console.log(`Increasing visible count from ${prev} to ${nextCount}`);
        return nextCount;
      });
    }
  }, [visibleArchives, loadedImages, hasMore, currentYear, currentArchives.length]);

  if (!items || isLoading) {
    return <Loader />;
  }

  return (
      <div className={styles.container}>
        <h1>Архив событий</h1>

        <div className={styles.yearNavigation}>
          {Object.keys(groupedArchives).map((year) => (
              <button
                  key={year}
                  className={`${styles.yearButton} ${currentYear === year ? styles.yearButtonActive : ''}`}
                  onClick={() => setCurrentYear(year)}
              >
                {year} год
              </button>
          ))}
        </div>

        {Object.entries(groupedArchives).map(([year, archives]) => (
            <div
                key={year}
                className={`${styles.yearSection} ${currentYear === year ? '' : styles.hidden}`}
            >
              <h2 className={styles.yearTitle}>{year} год</h2>

              <div className={styles.galleryGrid}>
                {visibleArchives.map((archive) => {
                  const imageId = `${year}-${archive.id}`;
                  const isLoaded = loadedImages.has(imageId);
                  const imageSrc = archive.imgCover || archive.img;

                  return (
                      <div
                          className={styles.galleryItem}
                          key={imageId}
                          onClick={() => handleImageClick(archive)}
                      >
                        {!isLoaded && (
                            <div className={styles.galleryItemLoader}>
                              <div className={styles.spinner} />
                            </div>
                        )}
                        <img
                            src={imageSrc}
                            alt={archive.name}
                            className={`${styles.galleryItemImage} ${isLoaded ? styles.galleryItemImageLoaded : ''}`}
                            onLoad={() => handleImageLoad(imageId)}
                            style={{ opacity: isLoaded ? 1 : 0 }}
                        />
                      </div>
                  );
                })}
              </div>

              {hasMore && (
                  <div className={styles.loadingMoreContainer}>
                    <div className={styles.loadingSpinner} />
                    <span>Загрузка изображений... ({visibleArchives.length} из {currentArchives.length})</span>
                  </div>
              )}
            </div>
        ))}

        {selectedImage && (
            <Modal isOpen={!!selectedImage} onClose={closeModal}>
              <div className={styles.zoomedImageContainer}>
                <Zoomable>
                  <img
                      src={selectedImage.src}
                      alt={selectedImage.alt}
                      className={styles.zoomedImage}
                  />
                </Zoomable>
              </div>
            </Modal>
        )}
      </div>
  );
};

export default OurEventPage;